/**
 * @file FiltersSidebar.jsx
 * @description Filtre yönetimi sidebar bileşeni
 *
 * Aktif filtreleri görüntüler ve inline düzenleme imkanı sunar.
 * Accordion tarzı genişleme ile filtre değerlerini seçme,
 * yeni filtre ekleme ve mevcut filtreleri kaldırma işlevleri.
 *
 * @module widget/components/FiltersSidebar
 */

import { useState, useCallback, useMemo } from "react";
import { X, ChevronDown, ChevronUp } from "lucide-react";
import { Checkbox } from "@openai/apps-sdk-ui/components/Checkbox";
import { getFilterLabel, getFilterValueLabel, getFilterOptions, formatNumber } from "../utils";
import { getThemeClass } from "../utils/theme";
import { AddFilterDropdown } from "./FilterEditor";
import { RoomSelector } from "./RoomSelector";

/**
 * Filtre Sidebar Bileşeni
 *
 * Emlakjet arama filtrelerini yönetmek için kullanılan panel.
 * Mevcut filtreleri pill (hap) şeklinde gösterir, tıklanınca
 * accordion açılır ve değerler düzenlenebilir.
 *
 * @component
 * @param {Object} props - Bileşen prop'ları
 * @param {Object.<string, string[]>} props.filters - Aktif filtreler
 *   Örnek: { "oda-sayisi": ["3-1", "4-1"], "min-fiyat": ["1000000"] }
 * @param {number} props.total - Toplam sonuç sayısı
 * @param {Function} props.onRemoveFilter - Filtre kaldırma callback'i (key)
 * @param {Function} [props.onAddFilter] - Yeni filtre ekleme callback'i (key)
 * @param {Function} props.onApplyFilter - Filtre uygulama callback'i (key, values[])
 * @param {boolean} props.isDark - Karanlık mod aktif mi
 *
 * @returns {JSX.Element} Filtre sidebar bileşeni
 *
 * @example
 * <FiltersSidebar
 *   filters={{ "oda-sayisi": ["3-1"] }}
 *   total={150}
 *   onRemoveFilter={(key) => removeFilter(key)}
 *   onApplyFilter={(key, values) => applyFilter(key, values)}
 *   isDark={false}
 * />
 *
 * @remarks
 * Oda sayısı filtresi özel RoomSelector bileşeni kullanır.
 * Diğer filtreler checkbox listesi olarak gösterilir.
 */
export function FiltersSidebar({
  filters,
  total,
  onRemoveFilter,
  onAddFilter,
  onApplyFilter,
  isDark
}) {
  // Sıralama aktif filtreler listesinde gösterilmemeli (SortDropdown zaten var)
  const HIDDEN_FILTER_KEYS = ["siralama"];
  const visibleFilters = filters
    ? Object.fromEntries(Object.entries(filters).filter(([key]) => !HIDDEN_FILTER_KEYS.includes(key)))
    : {};
  const hasFilters = Object.keys(visibleFilters).length > 0;
  const existingFilterKeys = hasFilters ? Object.keys(visibleFilters) : [];

  // Track which filter is expanded for inline editing
  const [expandedKey, setExpandedKey] = useState(null);
  const [selectedValues, setSelectedValues] = useState(new Set());

  // Handle filter pill click - toggle inline expansion
  const handleFilterClick = useCallback((key) => {
    if (expandedKey === key) {
      // Clicking same filter - collapse it
      setExpandedKey(null);
      setSelectedValues(new Set());
    } else {
      // Expand this filter with current values
      const currentValues = filters?.[key] || [];
      setExpandedKey(key);
      setSelectedValues(new Set(currentValues));
    }
  }, [expandedKey, filters]);

  // Handle adding a new filter
  const handleAddFilter = useCallback((key) => {
    setExpandedKey(key);
    setSelectedValues(new Set(filters?.[key] || []));
  }, [filters]);

  // Toggle individual value
  const toggleValue = useCallback((slug) => {
    setSelectedValues(prev => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  }, []);

  // Apply the filter changes
  const handleApply = useCallback(() => {
    if (expandedKey && onApplyFilter) {
      onApplyFilter(expandedKey, Array.from(selectedValues));
    }
    setExpandedKey(null);
    setSelectedValues(new Set());
  }, [expandedKey, selectedValues, onApplyFilter]);

  // Cancel editing
  const handleCancel = useCallback(() => {
    setExpandedKey(null);
    setSelectedValues(new Set());
  }, []);

  // Get options for expanded filter (memoized)
  const expandedOptions = useMemo(
    () => (expandedKey ? getFilterOptions(expandedKey) : []),
    [expandedKey]
  );
  const isRoomFilter = expandedKey === "oda-sayisi";

  // Memoized filter pills rendering
  const filterPills = useMemo(() => {
    if (!hasFilters) return null;
    return Object.entries(visibleFilters).map(([key, values]) => {
      const label = getFilterLabel(key);
      const firstValueLabel = values.length > 0 ? getFilterValueLabel(key, values[0]) : "";
      const valueText = values.length
        ? `${firstValueLabel}${values.length > 1 ? ` (+${values.length - 1})` : ""}`
        : "";
      const isExpanded = expandedKey === key;

      return (
        <span
          key={key}
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs cursor-pointer transition-all ${
            isExpanded
              ? isDark ? "bg-emerald-500/20 ring-1 ring-emerald-500/50" : "bg-emerald-500/10 ring-1 ring-emerald-500/30"
              : `${getThemeClass.bgElevated(isDark)} hover:ring-1 ${isDark ? "hover:ring-emerald-500/50" : "hover:ring-emerald-500/30"}`
          }`}
          onClick={() => handleFilterClick(key)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleFilterClick(key); }}
        >
          <span className={`font-medium ${getThemeClass.textStrong(isDark)}`}>{label}</span>
          {valueText && <span className={getThemeClass.textSecondary(isDark)}>{valueText}</span>}
          {isExpanded ? (
            <ChevronUp className="h-3 w-3" strokeWidth={2} />
          ) : (
            <ChevronDown className="h-3 w-3" strokeWidth={2} />
          )}
          <button
            type="button"
            className={`inline-flex h-4 w-4 items-center justify-center rounded-full ${isDark ? "hover:bg-white/20" : "hover:bg-black/20"}`}
            onClick={(e) => { e.stopPropagation(); onRemoveFilter(key); }}
            aria-label={`${label} filtresini kaldır`}
          >
            <X className="h-3 w-3" strokeWidth={2} />
          </button>
        </span>
      );
    });
  }, [visibleFilters, hasFilters, expandedKey, isDark, handleFilterClick, onRemoveFilter]);

  return (
    <div className={`p-4 border-b ${getThemeClass.border(isDark)}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`text-sm font-semibold ${getThemeClass.textStrong(isDark)}`}>
          {hasFilters ? "Aktif Filtreler" : "Filtreler"}
        </div>
        <div className={`text-xs ${getThemeClass.textMuted(isDark)}`}>
          {total > 0 ? `${formatNumber(total)} sonuç` : ""}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {filterPills}

        {/* Add Filter Button */}
        {onAddFilter && (
          <AddFilterDropdown
            onSelectFilter={handleAddFilter}
            existingFilters={existingFilterKeys}
            isDark={isDark}
          />
        )}
      </div>

      {/* Inline Expanded Filter Options */}
      {expandedKey && (
        <div className={`mt-3 pt-3 border-t ${getThemeClass.border(isDark)}`}>
          <div className={`text-xs font-medium mb-2 ${getThemeClass.textSecondary(isDark)}`}>
            {getFilterLabel(expandedKey)}
          </div>

          {isRoomFilter ? (
            <RoomSelector
              selectedValues={selectedValues}
              onToggle={toggleValue}
              isDark={isDark}
            />
          ) : expandedOptions.length === 0 ? (
            <div className={`text-xs p-2 rounded-lg ${getThemeClass.textMuted(isDark)} ${getThemeClass.bgSubtle(isDark)}`}>
              Bu filtre için seçenek bulunamadı.
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {expandedOptions.map(({ slug, label }) => {
                const isSelected = selectedValues.has(slug);
                return (
                  <button
                    key={slug}
                    type="button"
                    onClick={() => toggleValue(slug)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                      isSelected
                        ? isDark ? "bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/50" : "bg-emerald-500/10 text-emerald-700 ring-1 ring-emerald-500/30"
                        : `${getThemeClass.bgSubtle(isDark)} ${getThemeClass.textSecondary(isDark)} ${getThemeClass.bgHover(isDark)}`
                    }`}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggleValue(slug)}
                      className="h-3 w-3"
                    />
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Apply/Cancel buttons */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleCancel}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                getThemeClass.borderStrong(isDark)
              } ${getThemeClass.textPrimary(isDark)} ${getThemeClass.bgHover(isDark)}`}
            >
              İptal
            </button>
            <button
              onClick={handleApply}
              style={{ backgroundColor: '#059669', color: '#ffffff' }}
              className="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg hover:opacity-90 transition-opacity"
            >
              Uygula ({selectedValues.size})
            </button>
          </div>
        </div>
      )}

      {hasFilters && !expandedKey && (
        <div className={`mt-3 text-xs ${getThemeClass.textMuted(isDark)}`}>
          Filtreleri düzenlemek için üzerine tıklayın.
        </div>
      )}
    </div>
  );
}

export default FiltersSidebar;
